import React, { useState, useRef, useEffect, useContext } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from "chartjs-plugin-datalabels";
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import ClipLoader from 'react-spinners/ClipLoader';
import CONFIG from '../config.js';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const PieChart = ({ selected, labels, data, title, weekdayText, weekendText }) => {
    const { user, fetchData, setFetchData, isAdmin, adminSelectedUser } = useContext(AuthContext);
    let userInfo = user;
    if (isAdmin) {
        userInfo = adminSelectedUser;
    }
    const [activeElement, setActiveElement] = useState(null);
    const chartRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const pieChartModalRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [pieChartInsights, setPieChartInsights] = useState();
    const [activeTab, setActiveTab] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);
    const url = CONFIG.createUrl();

    const config = {headers: CONFIG.HEADERS};

    const fetchInsights = async () => {
        try {
            const [weeklyResponse, monthlyResponse, quarterlyResponse] = await axios.all([
                axios.get(`${url}weekday-weekend-insights?uuid=${userInfo.uuid}&start_date=${fetchData.weekStartDate}&end_date=${fetchData.maxDate}`, config),
                axios.get(`${url}weekday-weekend-insights?uuid=${userInfo.uuid}&start_date=${fetchData.monthStartDate}&end_date=${fetchData.maxDate}`, config),
                axios.get(`${url}weekday-weekend-insights?uuid=${userInfo.uuid}&start_date=${fetchData.quarterStartDate}&end_date=${fetchData.maxDate}`, config),
            ]);
            const newInsights = {
                weekly: weeklyResponse.data.recommendations,
                monthly: monthlyResponse.data.recommendations,
                quarterly: quarterlyResponse.data.recommendations
            }
            setPieChartInsights(newInsights);
            setFetchData(prev => ({ ...prev, pieChartInsightsData: newInsights }))
            setIsModalOpen(true);
            document.body.style.overflow = 'hidden'; // Prevent body scroll
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    const openModal = () => {
        setLoading(true);
        if (!fetchData.pieChartInsightsData) {
            fetchInsights();
        } else {
            setPieChartInsights(fetchData.pieChartInsightsData);
            setIsModalOpen(true);
            document.body.style.overflow = 'hidden';
            setLoading(false);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setActiveTab(0);
        document.body.style.overflow = 'auto'; // Restore body scroll
    };

    const handleClickOutside = (event) => {
        if (pieChartModalRef.current && !pieChartModalRef.current.contains(event.target)) {
            closeModal();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    const chartData = {
        labels: labels,
        datasets: [
            {
                label: title,
                data: data,
                backgroundColor: ['#9cd3d5', '#cdcdcd'],
                hoverOffset: 4,
            },
        ],
    };

    // Chart options
    const options = {
        plugins: {
            tooltip: {
                enabled: false,
            },
            datalabels: {
                color: "#fff",
                formatter: (value) => {
                    // Return the label for each section (or format as you like)
                    return `${value}%`;
                },
                font: {
                    weight: "bold",
                    size: 16
                },
            },
        },
        onClick: (event, elements) => {
            if (elements.length > 0) {
                const element = elements[0];
                setActiveElement(element);
            } else {
                setActiveElement(null);
            }
        },
        onHover: (event, elements) => {
            if (chartRef.current) {
                chartRef.current.style.cursor = elements.length > 0 ? 'pointer' : 'default';
            }
        },
    };

    // Handle click outside to close tooltip
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (chartRef.current && !chartRef.current.contains(event.target)) {
                setActiveElement(null);
            }
        };

        // Add event listener for clicks outside the chart
        document.addEventListener("click", handleClickOutside);

        return () => {
            // Cleanup the event listener
            document.removeEventListener("click", handleClickOutside);
        };
    }, [chartRef]);

    // const renderPieChartInsights = () => {
    //     const insightHeaders = Object.keys(pieChartInsights[selected]);
    //     return (
    //         <div className="dashboard-carousel">
    //             <h4 className="recommendation-header">CONCIERGE INSIGHTS</h4>
    //             <div className="tabs">
    //                 {insightHeaders.map((header, index) => (
    //                     <button
    //                         className={activeTab === index ? 'active' : ''}
    //                         onClick={() => setActiveTab(index)}
    //                         key={`pieChart-insights-tab-${index}`}
    //                     >
    //                         {header}
    //                     </button>
    //                 ))}
    //             </div>
    //             <div className="tab-content">
    //                 {insightHeaders.map((header, index) => (
    //                     <div key={`pieChart-insights-tabContent-${index}`}>
    //                         {activeTab === index &&
    //                             <ul>
    //                                 {pieChartInsights[selected][header].map((summaryItem, ind) => (
    //                                     <li key={`pieChart-insights-item-${ind}`}>{summaryItem}</li>
    //                                 ))}
    //                             </ul>
    //                         }
    //                     </div>
    //                 ))}
    //             </div>
    //         </div>
    //     );
    // }

    const goToPrevious = (slides) => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? slides.length - 1 : prevIndex - 1));
    };

    const goToNext = (slides) => {
        setCurrentIndex((prevIndex) => (prevIndex === slides.length - 1 ? 0 : prevIndex + 1));
    };
    const renderPieChartInsights = () => {
        const insightHeaders = Object.keys(pieChartInsights[selected]);
        return (
            <div>
                {insightHeaders.map((slide, index) => (
                    <div key={`pieChart-slide-${index}`} className="pieChart-insight-item">
                        <p className="pieChart-insights-header"><b>{slide}</b></p>
                        <ul>
                            {pieChartInsights[selected][slide].map((summaryItem, ind) => (
                                <li key={`pieChart-insights-item-${ind}`}>{summaryItem}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="pie-chart">
            <button className="ai-recco-button" onClick={openModal}>Concierge Insights</button>
            <h2 style={{ textAlign: 'center' }}>{title}</h2>
            <div ref={chartRef}>
                <Pie data={chartData} options={options} />
                {activeElement && (
                    <div className="pie-chart-tooltip">
                        <h4>Weekdays:</h4>
                        <p>{weekdayText}</p>
                        <h4>Weekends:</h4>
                        <p>{weekendText}</p>
                    </div>
                )}
            </div>

            {loading &&
                <div className="spinner-container">
                    <ClipLoader color={"#123abc"} loading={loading} size={50} />
                </div>
            }
            {isModalOpen && !loading && (
                <div className="modal-overlay">
                    <div className="modal-content " ref={pieChartModalRef}>
                        <button className="close-modal-button" onClick={closeModal}>×</button>
                        {/* <div className="recco-list">
                            {renderPieChartInsights()}
                        </div> */}
                        <div className="dashboard-carousel">
                            <h4 className="recommendation-header">CONCIERGE INSIGHTS</h4>
                            <div className="tab-content">
                                {renderPieChartInsights()}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PieChart;
